import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Mail, 
  MailOpen, 
  RefreshCw, 
  Loader, 
  AlertCircle, 
  CheckCircle, 
  Eye,
  Send,
  Calendar,
  User,
  FileText,
  Zap,
  X,
  Settings,
  MoreVertical
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { formatDateDisplay } from '../utils/dateUtils';
import { toast } from 'sonner';
import { useGroupedRecords, getGroupLabels, getGroupOrder } from '../hooks/useGroupedRecords';
import { GroupHeader } from '../components/GroupHeader';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';

// Helper component to render grouped emails
const EmailsGroupedView = ({ emails, onViewEmail }) => {
  // Normalize email dates for grouping
  const normalizedEmails = emails.map(email => ({
    ...email,
    date: email.date // emails already have date field
  }));
  
  const { groupedRecords, toggleGroup, isGroupCollapsed } = useGroupedRecords(normalizedEmails, 'date');
  const groupLabels = getGroupLabels();
  const groupOrder = getGroupOrder();

  return (
    <div className="space-y-4">
      {groupOrder.map(groupKey => {
        const groupEmails = groupedRecords[groupKey];
        if (groupEmails.length === 0) return null;
        
        const isCollapsed = isGroupCollapsed(groupKey);
        
        return (
          <Card key={groupKey}>
            <GroupHeader
              title={groupLabels[groupKey]}
              count={groupEmails.length}
              isCollapsed={isCollapsed}
              onToggle={() => toggleGroup(groupKey)}
            />
            
            {!isCollapsed && (
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {groupEmails.map((email, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border hover:shadow-md transition-shadow cursor-pointer ${
                        email.is_unread 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'bg-white border-gray-200'
                      }`}
                      onClick={() => onViewEmail(email)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          {email.is_unread ? (
                            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                          ) : (
                            <MailOpen className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className={`font-semibold truncate ${
                                email.is_unread ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {email.subject || '(No Subject)'}
                              </h3>
                              {email.is_unread && (
                                <Badge className="bg-blue-600 text-white">New</Badge>
                              )}
                              {email.processed && (
                                <Badge className="bg-green-600 text-white flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Processed
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                <span className="truncate">{email.from}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>{email.date}</span>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {email.body?.substring(0, 150)}...
                            </p>
                          </div>
                        </div>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewEmail(email);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

const Inbox = () => {
  const { isMobile } = useDeviceDetection();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [diagnosticsResults, setDiagnosticsResults] = useState(null);
  const [runningDiagnostics, setRunningDiagnostics] = useState(false);
  const [processingEmail, setProcessingEmail] = useState(false);

  useEffect(() => {
    fetchEmails();
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API}/inbox/connection-status`, {
        headers: getAuthHeader()
      });
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Error checking connection:', error);
      setConnectionStatus({ connected: false });
    }
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      // Fetch emails and draft fines in parallel
      const [emailsResponse, draftsResponse] = await Promise.all([
        axios.get(`${API}/inbox/emails?limit=100`, { headers: getAuthHeader() }),
        axios.get(`${API}/draft-fines`, { headers: getAuthHeader() })
      ]);
      
      const emails = emailsResponse.data.emails || [];
      const drafts = draftsResponse.data.drafts || [];
      
      // Create a set of processed email IDs
      const processedEmailIds = new Set(drafts.map(d => d.email_id).filter(Boolean));
      
      // Mark emails that have been processed
      const emailsWithStatus = emails.map(email => ({
        ...email,
        processed: processedEmailIds.has(email.email_id),
        draft_fine_id: drafts.find(d => d.email_id === email.email_id)?.id
      }));
      
      setEmails(emailsWithStatus);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Failed to fetch emails. Check email connection.');
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setViewDialogOpen(true);
  };

  const runDiagnostics = async () => {
    setRunningDiagnostics(true);
    setDiagnosticsResults(null);
    try {
      const response = await axios.post(`${API}/inbox/test-connection`, {}, {
        headers: getAuthHeader()
      });
      setDiagnosticsResults(response.data);
      setDiagnosticsOpen(true);
    } catch (error) {
      console.error('Error running diagnostics:', error);
      toast.error('Failed to run diagnostics');
    } finally {
      setRunningDiagnostics(false);
    }
  };

  const processEmailToFine = async (email) => {
    setProcessingEmail(true);
    try {
      const response = await axios.post(`${API}/inbox/process-email`, {
        email_id: email.email_id,
        subject: email.subject,
        from: email.from,
        date: email.date,
        body: email.body
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Email processed! Draft fine created.');
      
      // Update the email in the list to show it's processed
      setEmails(prevEmails => 
        prevEmails.map(e => 
          e.email_id === email.email_id 
            ? { ...e, processed: true, draft_fine_id: response.data.draft_id }
            : e
        )
      );
      
      // Update the selected email
      setSelectedEmail(prev => ({ ...prev, processed: true, draft_fine_id: response.data.draft_id }));
      
    } catch (error) {
      console.error('Error processing email:', error);
      toast.error(formatErrorMessage(error, 'Failed to process email'));
    } finally {
      setProcessingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 animate-spin text-purple-500" />
        <span className="ml-3 text-gray-600">Loading inbox...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Inbox</h1>
          <p className="text-slate-600 mt-1">
            Monitor and process fine notification emails
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchEmails} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Link to="/settings">
            <Button variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Connection Status */}
      <Card className={`border-l-4 ${connectionStatus?.connected ? 'border-l-green-500 bg-green-50' : 'border-l-red-500 bg-red-50'}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {connectionStatus?.connected ? (
                <>
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <div className="font-semibold text-green-800">Email Connected</div>
                    <div className="text-sm text-green-700">
                      {connectionStatus.email} → {connectionStatus.server}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <div className="font-semibold text-red-800">Email Connection Failed</div>
                    <div className="text-sm text-red-700">
                      Please check IMAP settings and credentials in Microsoft 365 admin
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={checkConnection}
              >
                Test Connection
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={runDiagnostics}
                disabled={runningDiagnostics}
              >
                {runningDiagnostics ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  'Run Diagnostics'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Emails</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-800">{emails.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Unread</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {emails.filter(e => e.is_unread).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Read</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-600">
              {emails.filter(e => !e.is_unread).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email List */}
      {emails.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Mail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Emails Found</h3>
              <p className="text-sm text-gray-500">
                {connectionStatus?.connected 
                  ? 'Your inbox is empty or emails could not be fetched.'
                  : 'Please check your email connection settings.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <EmailsGroupedView emails={emails} onViewEmail={handleViewEmail} />
      )}

      {/* View Email Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Email Details
            </DialogTitle>
            <DialogDescription>
              View full email content and process into draft fine
            </DialogDescription>
          </DialogHeader>

          {selectedEmail && (
            <div className="space-y-6">
              {/* Email Metadata */}
              <Card className="bg-gray-50">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">Subject</div>
                        <div className="font-semibold text-gray-800">
                          {selectedEmail.subject || '(No Subject)'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">From</div>
                        <div className="text-gray-800">{selectedEmail.from}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">Received</div>
                        <div className="text-gray-800">{selectedEmail.date}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Send className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">To</div>
                        <div className="text-gray-800">ERP@ezytronix.com</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email Body */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Email Content</h3>
                <div className="p-4 bg-white border rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                    {selectedEmail.body}
                  </pre>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {selectedEmail.is_unread ? (
                    <Badge className="bg-blue-600">Unread</Badge>
                  ) : (
                    <Badge variant="outline">Read</Badge>
                  )}
                  {selectedEmail.processed && (
                    <Badge className="bg-green-600 text-white flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Processed - Draft Created
                    </Badge>
                  )}
                </div>

                <div className="flex gap-2">
                  {!selectedEmail.processed && (
                    <Button
                      onClick={() => processEmailToFine(selectedEmail)}
                      disabled={processingEmail}
                      className="bg-lime-600 hover:bg-lime-700"
                    >
                      {processingEmail ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Process to Draft Fine
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setViewDialogOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diagnostics Dialog */}
      <Dialog open={diagnosticsOpen} onOpenChange={setDiagnosticsOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Email Connection Diagnostics
            </DialogTitle>
            <DialogDescription>
              Detailed test results for email connection
            </DialogDescription>
          </DialogHeader>

          {diagnosticsResults && (
            <div className="space-y-4">
              {/* Overall Status */}
              <Card className={`border-l-4 ${
                diagnosticsResults.overall_status === 'success' 
                  ? 'border-l-green-500 bg-green-50' 
                  : 'border-l-red-500 bg-red-50'
              }`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    {diagnosticsResults.overall_status === 'success' ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    )}
                    <div>
                      <div className={`font-bold ${
                        diagnosticsResults.overall_status === 'success' 
                          ? 'text-green-800' 
                          : 'text-red-800'
                      }`}>
                        {diagnosticsResults.overall_status === 'success' 
                          ? 'All Tests Passed!' 
                          : 'Connection Issues Detected'}
                      </div>
                      <div className={`text-sm ${
                        diagnosticsResults.overall_status === 'success' 
                          ? 'text-green-700' 
                          : 'text-red-700'
                      }`}>
                        {diagnosticsResults.overall_status === 'success'
                          ? 'Your email connection is working perfectly'
                          : 'Please follow the troubleshooting steps below'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Test Steps */}
              <div className="space-y-2">
                {diagnosticsResults.tests.map((test) => (
                  <Card key={test.step} className={`${
                    test.status === 'success' 
                      ? 'border-green-200' 
                      : test.status === 'failed'
                      ? 'border-red-200'
                      : 'border-gray-200'
                  }`}>
                    <CardContent className="py-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          test.status === 'success'
                            ? 'bg-green-100 text-green-600'
                            : test.status === 'failed'
                            ? 'bg-red-100 text-red-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {test.status === 'success' ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : test.status === 'failed' ? (
                            <X className="w-5 h-5" />
                          ) : (
                            <Loader className="w-5 h-5 animate-spin" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">
                            Step {test.step}: {test.name}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {test.message}
                          </div>
                          {test.help && (
                            <div className="mt-2 p-3 bg-blue-50 rounded text-sm text-blue-800 border border-blue-200">
                              <strong>💡 Solution:</strong> {test.help}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Troubleshooting Guide */}
              {diagnosticsResults.overall_status === 'failed' && (
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardHeader>
                    <CardTitle className="text-sm text-yellow-800">
                      📋 Troubleshooting Steps
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="text-sm text-yellow-800 space-y-2 list-decimal list-inside">
                      <li>
                        <strong>Enable IMAP in Microsoft 365:</strong>
                        <br />
                        <span className="ml-6 text-xs">
                          Admin Center → Users → Active users → ERP@ezytronix.com → Mail → Manage email apps → Check IMAP
                        </span>
                      </li>
                      <li>
                        <strong>Wait 5-15 minutes</strong> for changes to propagate
                      </li>
                      <li>
                        <strong>Enable Basic Authentication:</strong>
                        <br />
                        <span className="ml-6 text-xs">
                          Exchange Admin Center → Settings → Authentication policies
                        </span>
                      </li>
                      <li>
                        <strong>Verify credentials</strong> by logging into:
                        <br />
                        <span className="ml-6 text-xs">
                          https://outlook.office365.com/mail/
                        </span>
                      </li>
                    </ol>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDiagnosticsOpen(false)}>
                  Close
                </Button>
                <Button onClick={runDiagnostics} disabled={runningDiagnostics}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Again
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inbox;
